// caiet UC21-02 / UC21-05 / UC21-06 — what an administrator may type about a
// Registrar, and the rules it has to meet. Enrolment and the correction
// dialog read the same rules from here, so the form cannot accept what the
// dialog refuses, or the other way round.

// The type is one of the shared `REGISTRAR_KINDS`, the list catalog-service holds
// its column to, refusing anything else with INVALID_REGISTRAR_KIND.
import { registrarCodeOf, registrarKindSchema, type RegistrarKind } from '@simsm/shared-types';

export const IDNO_LENGTH = 13;

// The code MPass uses for the organisation is the Registrar's IDNO followed by 0000
// (UC21-05, NC-UC10-01), which is what catalog-service accepts and what the database
// derives; a form refuses any other instead of round-tripping it.

export function isRegistrarKind(value: string): value is RegistrarKind {
  return registrarKindSchema.safeParse(value).success;
}

// The wireframe strips anything that is not a digit as it is typed
// (RegistratorDetalii.tsx:644), so a code pasted with spaces still fits.
export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

export interface CorrectionErrors {
  kind?: 'required';
  mpassOrgCode?: 'invalid';
}

export function validateCorrection(input: {
  idno: string;
  kind: string;
  mpassOrgCode: string;
}): CorrectionErrors {
  return {
    ...(isRegistrarKind(input.kind) ? {} : { kind: 'required' as const }),
    ...(input.mpassOrgCode === registrarCodeOf(input.idno)
      ? {}
      : { mpassOrgCode: 'invalid' as const }),
  };
}

export function hasCorrectionErrors(errors: CorrectionErrors): boolean {
  return errors.kind !== undefined || errors.mpassOrgCode !== undefined;
}

// The same record the wireframe keeps (RegistratorDetalii.tsx:112-114): one
// line per field that moved, in the order the file shows them.
export interface CorrectionChange {
  field: 'mpassOrgCode' | 'kind';
  before: string;
  after: string;
}

// CF-47: the Registrar's data is visible by its current type, and the IMSP type is the one
// Observers see at detail level and the stock check lists, so a type change moves its whole
// history out of those views, into them, or leaves them as they are.
export type KindChangeVisibility = 'hidden' | 'shown' | 'unchanged';

export function kindChangeVisibility(before: string, after: string): KindChangeVisibility {
  if (before === 'IMSP' && after !== 'IMSP') return 'hidden';
  if (before !== 'IMSP' && after === 'IMSP') return 'shown';
  return 'unchanged';
}

export function correctionChanges(
  before: { kind: string; mpassOrgCode: string },
  after: { kind: string; mpassOrgCode: string },
): CorrectionChange[] {
  return [
    ...(before.mpassOrgCode !== after.mpassOrgCode
      ? [{ field: 'mpassOrgCode' as const, before: before.mpassOrgCode, after: after.mpassOrgCode }]
      : []),
    ...(before.kind !== after.kind
      ? [{ field: 'kind' as const, before: before.kind, after: after.kind }]
      : []),
  ];
}
